import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

/**
 * Super-admin dialog to edit the meta fields of a round (visit_date only for
 * now — that's the field admins actually need to correct). Kept intentionally
 * small: bigger-scope edits happen through Reopen + the normal wizard.
 */
export default function EditRoundMetaDialog({ round, onClose, onSaved }) {
    const { t } = useI18n();
    const [visitDate, setVisitDate] = useState("");
    const [saving, setSaving] = useState(false);
    const open = !!round;

    useEffect(() => {
        if (round) setVisitDate((round.visit_date || "").slice(0, 10));
    }, [round]);

    const save = async () => {
        if (!round) return;
        setSaving(true);
        try {
            await api.patch(`/qc-inspection/inspections/${round.id}/admin`, {
                visit_date: visitDate || null,
            });
            toast.success(t("save_success"));
            onSaved && (await onSaved());
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Pencil className="h-4 w-4 text-[color:var(--brand-orange)]" />
                        {t("qc_admin_edit_meta_title")}
                    </DialogTitle>
                    <DialogDescription>{t("qc_admin_edit_meta_desc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>{t("qc_ins_visit_date")}</Label>
                        <Input
                            data-testid={OPS.qcIns.rounds.admin.editVisitDate}
                            type="date"
                            value={visitDate}
                            onChange={(e) => setVisitDate(e.target.value)}
                            dir="ltr"
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        {t("cancel")}
                    </Button>
                    <Button
                        data-testid={OPS.qcIns.rounds.admin.editSave}
                        onClick={save}
                        disabled={saving}
                    >
                        {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                        {t("save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
